import { ArrowRightOutlined, ExportOutlined } from "@ant-design/icons";
import { Card } from "antd";
import { createStyles } from "antd-style";
import { Link } from "react-router-dom";
import type { DashboardActionsCardComponent } from "@rumbledirect/types";
import { resolveActionIcon } from "../actionHelpers";

interface Props {
  component: DashboardActionsCardComponent;
}

const useStyles = createStyles(({ css, token }) => ({
  grid: css`
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
  `,
  tile: css`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    border: 1px solid ${token.colorBorderSecondary};
    border-radius: ${token.borderRadiusLG}px;
    background: ${token.colorBgContainer};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: ${token.colorPrimary};
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
      transform: translateY(-2px);
    }

    &:hover .tile-arrow {
      color: ${token.colorPrimary};
      transform: translateX(2px);
    }
  `,
  iconBox: css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: ${token.borderRadius}px;
    background: ${token.colorPrimaryBg};
    color: ${token.colorPrimary};
    font-size: 20px;
  `,
  label: css`
    flex: 1;
    font-weight: 600;
    color: ${token.colorText};
    line-height: 1.4;
  `,
  arrow: css`
    flex-shrink: 0;
    color: ${token.colorTextTertiary};
    transition: all 0.2s ease;
  `,
}));

export function ActionsCardRenderer({ component }: Props) {
  const { styles, cx } = useStyles();

  return (
    <Card title={component.title}>
      <div className={styles.grid}>
        {component.actions.map((action) => {
          if (!action.url) {
            return null;
          }

          const external = action.external ?? false;

          const tileContent = (
            <>
              <span className={styles.iconBox}>
                {resolveActionIcon(action.icon)}
              </span>
              <span className={styles.label}>{action.text}</span>
              <span className={cx(styles.arrow, "tile-arrow")}>
                {external ? <ExportOutlined /> : <ArrowRightOutlined />}
              </span>
            </>
          );

          if (external) {
            return (
              <a
                key={action.url}
                className={styles.tile}
                href={action.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {tileContent}
              </a>
            );
          }

          return (
            <Link key={action.url} to={action.url} className={styles.tile}>
              {tileContent}
            </Link>
          );
        })}
      </div>
    </Card>
  );
}
